import type { Process } from "../../api/types";
import { getBrowserHash, splitHash, updateBrowserHash, updateHash } from "../../utils/urlHash";
import { validDate } from "../dashboard/dateSelection";
import { PROCESS_PANEL_TABS, type ProcessPanelTab } from "./ProcessPanelTabs";
import type {
  ComparisonGroup,
  ComparisonMode,
  DurationChoice,
  ProcessComparison,
} from "./processComparison";
import { EMPTY_INSTANCE_SCOPE, type InstanceScope } from "./processScopeModel";

// The process detail page's view state, one flat hash param per piece and each absent while at
// its default. Id lists are comma-joined (ids never contain commas); app names repeat the key.
export const PROCESS_TAB_HASH_KEY = "tab";
export const PROCESS_STEP_HASH_KEY = "step";
export const PROCESS_CASE_HASH_KEY = "case";
export const PROCESS_OCCURRENCE_HASH_KEY = "occurrence";
export const PROCESS_COMPARE_HASH_KEY = "compare";
export const PROCESS_COMPARISON_HASH_KEY = "comparison";
const SCOPE_KEYS = {
  commonPaths: "common",
  apps: "apps",
  /** Seconds, `min-max` with either side open. */
  duration: "duration",
  /** Step count, `min-max` with either side open. */
  length: "length",
  nodes: "nodes",
  excluded: "excluded",
  /** A page override of `pu`, in its format: `all`, empty for nobody, or ids. */
  people: "people",
  /** A page override of the dates, `from..to` with either side open, or `all`. */
  dates: "dates",
  query: "q",
  queryIds: "qIds",
  queryWhy: "qWhy",
} as const;
export type ProcessScopeHashKey = (typeof SCOPE_KEYS)[keyof typeof SCOPE_KEYS];

export const readProcessTab = (hash = getBrowserHash()): ProcessPanelTab => {
  const tab = splitHash(hash).params.get(PROCESS_TAB_HASH_KEY);
  return PROCESS_PANEL_TABS.find(known => known === tab) ?? "overview";
};
export const writeProcessTab = (tab: ProcessPanelTab) =>
  updateBrowserHash({ [PROCESS_TAB_HASH_KEY]: tab === "overview" ? null : tab });

export const readProcessStep = (hash = getBrowserHash()): string | null =>
  splitHash(hash).params.get(PROCESS_STEP_HASH_KEY) || null;
export const writeProcessStep = (nodeId: string | null) =>
  updateBrowserHash({ [PROCESS_STEP_HASH_KEY]: nodeId });

/** A case's timeline position is zero-based, so repeated steps remain distinct. */
export const readProcessCase = (hash = getBrowserHash()) => {
  const { params } = splitHash(hash);
  const instanceId = params.get(PROCESS_CASE_HASH_KEY);
  const rawIndex = params.get(PROCESS_OCCURRENCE_HASH_KEY);
  const stepIndex = rawIndex === null ? 0 : /^\d+$/.test(rawIndex) ? Number(rawIndex) : NaN;
  return instanceId && Number.isSafeInteger(stepIndex) && stepIndex >= 0
    ? { instanceId, stepIndex }
    : null;
};
export const writeProcessCase = (value: ReturnType<typeof readProcessCase>) =>
  updateBrowserHash({
    [PROCESS_CASE_HASH_KEY]: value?.instanceId ?? null,
    [PROCESS_OCCURRENCE_HASH_KEY]: value ? String(value.stepIndex) : null,
  });

/** Keep the underlying scope in the link; compare adds its own identities over that view. */
export const processComparisonHash = (
  hash: string,
  comparison: [string, string] | ProcessComparison,
) =>
  updateHash(hash, {
    [PROCESS_COMPARE_HASH_KEY]: Array.isArray(comparison) ? comparison.join(",") : null,
    [PROCESS_COMPARISON_HASH_KEY]: Array.isArray(comparison)
      ? null
      : serializeComparison(comparison),
    [PROCESS_STEP_HASH_KEY]: null,
    [PROCESS_CASE_HASH_KEY]: null,
    [PROCESS_OCCURRENCE_HASH_KEY]: null,
  });

/**
 * The two instances being compared, in the order they were picked, or null. The caller has to
 * check both ids against the loaded process.
 */
export const readProcessComparePair = (hash = getBrowserHash()): [string, string] | null => {
  const ids = splitHash(hash).params.get(PROCESS_COMPARE_HASH_KEY)?.split(",").filter(Boolean);
  return ids?.length === 2 && ids[0] !== ids[1] ? [ids[0], ids[1]] : null;
};
export const writeProcessComparePair = (pair: [string, string] | null) =>
  updateBrowserHash({ [PROCESS_COMPARE_HASH_KEY]: pair ? pair.join(",") : null });

type People = Pick<InstanceScope, "userIds" | "noUsers">;
type Dates = Pick<InstanceScope, "from" | "to">;
/** The page's people and dates, each present only where it departs from the inherited ones. */
export type PageOverride = Partial<People & Dates>;
export type ProcessUrlScope = { scope: InstanceScope; override: PageOverride };

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every(id => b.includes(id));
export const pageOverride = (
  scope: InstanceScope,
  inherited: People & Dates,
): PageOverride | null => {
  const people =
    !!scope.noUsers !== !!inherited.noUsers || !sameIds(scope.userIds, inherited.userIds);
  const dates = scope.from !== inherited.from || scope.to !== inherited.to;
  if (!people && !dates) return null;
  return {
    ...(people && { userIds: scope.userIds, noUsers: !!scope.noUsers }),
    ...(dates && { from: scope.from, to: scope.to }),
  };
};

const ids = (value: string) => value.split(",").filter(Boolean);
const readRange = (value: string | null): [number?, number?] | null => {
  const bounds = value?.split("-").map(bound => (bound ? Number(bound) : undefined));
  return bounds?.length === 2 &&
    bounds.every(bound => bound === undefined || (Number.isFinite(bound) && bound >= 0))
    ? [bounds[0], bounds[1]]
    : null;
};
const writeRange = (min?: number, max?: number) =>
  min === undefined && max === undefined ? null : `${min ?? ""}-${max ?? ""}`;
const readPeople = (value: string): People =>
  value === "all" ? { userIds: [], noUsers: false } : { userIds: ids(value), noUsers: !value };
const writePeople = ({ userIds, noUsers }: People) =>
  noUsers ? "" : userIds.length ? userIds.join(",") : "all";
const readDates = (value: string): Dates | null => {
  if (value === "all") return { from: null, to: null };
  const bounds = value.split("..");
  if (bounds.length !== 2) return null;
  const [from, to] = bounds.map(day => day || null);
  if ([from, to].some(day => day !== null && !validDate(day))) return null;
  return from && to && from > to ? null : { from, to };
};
const writeDates = ({ from, to }: Dates) => (from || to ? `${from ?? ""}..${to ?? ""}` : "all");

/** The scope a link names, or null when it names none. A malformed param drops only its filter. */
export function readProcessUrlScope(hash = getBrowserHash()): ProcessUrlScope | null {
  const { params } = splitHash(hash);
  if (!Object.values(SCOPE_KEYS).some(key => params.has(key))) return null;
  const scope: InstanceScope = { ...EMPTY_INSTANCE_SCOPE };
  const common = params.get(SCOPE_KEYS.commonPaths);
  if (common === "1" || common === "0") scope.commonPaths = common === "1";
  const apps = params.getAll(SCOPE_KEYS.apps).filter(Boolean);
  if (apps.length) scope.apps = apps;
  const duration = readRange(params.get(SCOPE_KEYS.duration));
  if (duration) [scope.minDuration, scope.maxDuration] = duration;
  const length = readRange(params.get(SCOPE_KEYS.length));
  if (length) [scope.minSteps, scope.maxSteps] = length;
  scope.nodeIds = ids(params.get(SCOPE_KEYS.nodes) ?? "");
  scope.excludedIds = ids(params.get(SCOPE_KEYS.excluded) ?? "");
  const text = params.get(SCOPE_KEYS.query);
  const queryIds = params.get(SCOPE_KEYS.queryIds);
  if (text && queryIds !== null)
    scope.query = {
      text,
      instanceIds: ids(queryIds),
      explanation: params.get(SCOPE_KEYS.queryWhy),
    };
  const people = params.get(SCOPE_KEYS.people);
  const dates = params.get(SCOPE_KEYS.dates);
  return {
    scope,
    override: {
      ...(people !== null && readPeople(people)),
      ...(dates !== null && readDates(dates)),
    },
  };
}

/**
 * Every scope param, cleared where its criterion is off. People and dates come from the override
 * where it has them, otherwise from the scope itself when it filters on them.
 */
export function processScopeHashParams(scope: InstanceScope, override: PageOverride = {}) {
  const people =
    override.userIds !== undefined
      ? { userIds: override.userIds, noUsers: override.noUsers }
      : scope.noUsers || scope.userIds.length
        ? scope
        : null;
  const dates =
    override.from !== undefined
      ? { from: override.from, to: override.to ?? null }
      : scope.from || scope.to
        ? scope
        : null;
  return {
    [SCOPE_KEYS.commonPaths]:
      scope.commonPaths === undefined ? null : scope.commonPaths ? "1" : "0",
    [SCOPE_KEYS.apps]: scope.apps?.length ? scope.apps : null,
    [SCOPE_KEYS.duration]: writeRange(scope.minDuration, scope.maxDuration),
    [SCOPE_KEYS.length]: writeRange(scope.minSteps, scope.maxSteps),
    [SCOPE_KEYS.nodes]: scope.nodeIds.join(",") || null,
    [SCOPE_KEYS.excluded]: scope.excludedIds.join(",") || null,
    [SCOPE_KEYS.people]: people && writePeople(people),
    [SCOPE_KEYS.dates]: dates && writeDates(dates),
    [SCOPE_KEYS.query]: scope.query?.text ?? null,
    [SCOPE_KEYS.queryIds]: scope.query?.instanceIds.join(",") ?? null,
    [SCOPE_KEYS.queryWhy]: scope.query?.explanation ?? null,
  };
}

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === "string");
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const parse = (value: string | null): unknown => {
  try {
    return value === null ? null : JSON.parse(value);
  } catch {
    return null;
  }
};

type GroupLink = { slug: string; label: string; ids: string[] };
export type ComparisonLink = {
  v: 1;
  mode: ComparisonMode;
  duration?: DurationChoice;
  a: GroupLink;
  b: GroupLink;
  excluded?: number;
  useAll?: boolean;
  period?: { a: [string, string]; b: [string, string] };
};
export function serializeComparison(comparison: ProcessComparison): string {
  const group = ({ process, label, runs }: ComparisonGroup): GroupLink => ({
    slug: process.slug,
    label,
    ids: runs.map(run => run.id),
  });
  return JSON.stringify({
    v: 1,
    mode: comparison.mode,
    duration: comparison.duration,
    a: group(comparison.a),
    b: group(comparison.b),
    excluded: comparison.excluded,
    useAll: comparison.useAll,
    period: comparison.period,
  });
}
export function readComparisonLink(hash = getBrowserHash()): ComparisonLink | null {
  const value = parse(splitHash(hash).params.get(PROCESS_COMPARISON_HASH_KEY));
  if (
    !object(value) ||
    value.v !== 1 ||
    typeof value.mode !== "string" ||
    !["duration", "people", "period", "runs", "apps", "processes"].includes(value.mode)
  )
    return null;
  for (const group of [value.a, value.b]) {
    if (
      !object(group) ||
      typeof group.slug !== "string" ||
      !group.slug ||
      typeof group.label !== "string" ||
      !strings(group.ids) ||
      !group.ids.length
    )
      return null;
  }
  if (value.useAll !== undefined && typeof value.useAll !== "boolean") return null;
  if (
    value.duration !== undefined &&
    (typeof value.duration !== "string" ||
      !["longerTypical", "shorterTypical", "longerShorter"].includes(value.duration))
  )
    return null;
  if (
    value.excluded !== undefined &&
    (typeof value.excluded !== "number" || !Number.isFinite(value.excluded) || value.excluded < 0)
  )
    return null;
  if (value.period !== undefined) {
    if (!object(value.period)) return null;
    for (const period of [value.period.a, value.period.b]) {
      if (
        !strings(period) ||
        period.length !== 2 ||
        period.some(date => !Number.isFinite(Date.parse(date))) ||
        Date.parse(period[0]) >= Date.parse(period[1])
      )
        return null;
    }
  }
  return value as ComparisonLink;
}

/** URLs contain identities, never cached graphs; fetch each distinct process once. */
export async function resolveComparisonLink(
  link: ComparisonLink,
  process: Process,
  load: (slug: string) => Promise<Process>,
): Promise<ProcessComparison> {
  const processes = new Map([[process.slug, Promise.resolve(process)]]);
  const group = async (value: GroupLink): Promise<ComparisonGroup> => {
    if (!processes.has(value.slug)) processes.set(value.slug, load(value.slug));
    const detail = await processes.get(value.slug)!;
    const byId = new Map(detail.instances.map(run => [run.id, run]));
    const runs = value.ids.map(id => byId.get(id));
    if (runs.some(run => !run))
      throw new Error("Some cases in this comparison are no longer available.");
    return { process: detail, label: value.label, runs: runs as Process["instances"] };
  };
  const [a, b] = await Promise.all([group(link.a), group(link.b)]);
  const period = (dates: [string, string]): [Date, Date] =>
    dates.map(date => new Date(date)) as [Date, Date];
  return {
    mode: link.mode,
    duration: link.duration,
    a,
    b,
    excluded: link.excluded,
    useAll: link.useAll,
    period: link.period ? { a: period(link.period.a), b: period(link.period.b) } : undefined,
  };
}
